import React from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

// Width presets carried over from the old Bootstrap-style modal sizes.
const SIZE_MAP = {
  'modal-sm': 'sm:max-w-sm',
  'modal-md': 'sm:max-w-lg',
  'modal-lg': 'sm:max-w-3xl',
  'modal-xl': 'sm:max-w-6xl',
}

// Long enough for the Radix close animation (duration-100) to finish before
// the caller unmounts us.
const EXIT_MS = 150

// Some widgets portal themselves onto document.body, so Radix's
// DismissableLayer counts clicks on them as "outside" the dialog and would
// dismiss it (or swallow the click). Veto dismissal for:
// - .pac-container: Google Places Autocomplete suggestion list
// - .bootstrap-datetimepicker-widget: legacy jQuery datetime picker (still in
//   use until a later migration phase)
const isVetoedEvent = (event) => {
  const target = event?.detail?.originalEvent?.target || event?.target
  if (!target || !target.closest) return false
  return !!target.closest('.pac-container, .bootstrap-datetimepicker-widget')
}

// Thin wrapper around the shadcn/Radix Dialog. Callers still mount <Modal> to
// open it and unmount it (via onClose) to close — exactly as before. The
// Dialog is driven by internal `open` state so its exit animation can play
// before onClose fires. Radix handles the portal, focus trap, Escape key,
// overlay-click and background scroll-lock (with scrollbar compensation), so
// none of that lives here anymore.
class Modal extends React.Component {
  constructor(props) {
    super(props)
    this.state = { open: true }
    this.onClose = this.onClose.bind(this)
    this.onOpenChange = this.onOpenChange.bind(this)
  }

  componentWillUnmount() {
    if (this.exitTimer) clearTimeout(this.exitTimer)
  }

  onClose() {
    if (this.closing) return
    this.closing = true
    this.setState({ open: false })
    this.exitTimer = setTimeout(() => this.props.onClose?.(), EXIT_MS)
  }

  onOpenChange(open) {
    if (!open) this.onClose()
  }

  render() {
    const { title, subtitle, children, size = 'modal-xl' } = this.props
    const widthClass = SIZE_MAP[size] || SIZE_MAP['modal-xl']

    return (
      <Dialog open={this.state.open} onOpenChange={this.onOpenChange}>
        <DialogContent
          {...(subtitle ? {} : { 'aria-describedby': undefined })}
          onPointerDownOutside={(e) => { if (isVetoedEvent(e)) e.preventDefault() }}
          onInteractOutside={(e) => { if (isVetoedEvent(e)) e.preventDefault() }}
          className={`top-[7vh] flex max-h-[86vh] translate-y-0 flex-col gap-0 overflow-hidden p-0 ${widthClass}`}
        >
          {title ? (
            <DialogHeader className="shrink-0 px-6 pt-6 pb-2">
              <DialogTitle>{title}</DialogTitle>
              {subtitle ? <DialogDescription>{subtitle}</DialogDescription> : null}
            </DialogHeader>
          ) : (
            <DialogTitle className="sr-only">Dialog</DialogTitle>
          )}
          <div className="min-h-0 flex-1 overflow-y-auto">
            {children}
          </div>
        </DialogContent>
      </Dialog>
    )
  }
}

export default Modal
